import {ChangeDetectionStrategy, Component, computed, Injectable, Input, signal, WritableSignal} from "@angular/core";
import {FormsModule} from "@angular/forms";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatButtonModule} from "@angular/material/button";
import {TranslateModule} from "@ngx-translate/core";
import {Autentiseringsmetode, BrukerModell, EnkelBrukerModell, GruppeId, GruppeModell, GruppeRessursClient, Reautentiseringsinformasjon, TilgangsrolleMedlem, TillatteAutentiseringsmetoderOppdatertModell, TillatteAutentiseringsmetoderSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {BrukerkontekstAdministrasjon} from "felles-ng2/tjenester/BrukerkontekstAdministrasjon";
import {Dialog} from "felles-ng2/tjenester/Dialog";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {MatDialog} from "@angular/material/dialog";
import {Autentiser, ReautentiseringDialogKomponent} from "web-ng2/moduler/autentisering/modale/reautentisering-dialog/reautentisering-dialog";
import {MatRadioButton, MatRadioGroup} from "@angular/material/radio";
import {MatIcon} from "@angular/material/icon";

@Component({
    selector: 'gruppe-tillatte-autentiseringsmetoder',
    templateUrl: './gruppe-tillatte-autentiseringsmetoder.html',
    styleUrl: './gruppe-tillatte-autentiseringsmetoder.scss',
    imports: [
        FormsModule,
        MatCheckboxModule,
        MatButtonModule,
        TranslateModule,
        MatRadioGroup,
        MatRadioButton,
        MatIcon
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GruppeTillatteAutentiseringsmetoderKomponent {
    static ajsSelector = 'gruppeTillatteAutentiseringsmetoder';

    Autentiseringsmetode = Autentiseringsmetode;

    // kan ikke være input. Kalles fra ajs som promise.
    // gruppeModell = input<GruppeModell|null>(null);
    gruppeModell!: GruppeModell;
    modus: WritableSignal<'vis' | 'rediger'> = signal('vis');
    kanRedigere = signal(false);
    metoder: MetodeAktiverHosBruker[] = [
        {metode: Autentiseringsmetode.passord, aktivertHosBruker: true},
        {metode: Autentiseringsmetode.smsOtp, aktivertHosBruker: true},
        {metode: Autentiseringsmetode.totpOtp, aktivertHosBruker: true},
        {metode: Autentiseringsmetode.criiptoOpenId, aktivertHosBruker: true}
    ]

    readonly alleMetoderSortertEtterinnloggingsNivaa = [Autentiseringsmetode.passord, Autentiseringsmetode.smsOtp, Autentiseringsmetode.totpOtp, Autentiseringsmetode.criiptoOpenId];
    valgtInnlogging = signal(Autentiseringsmetode.passord);
    valgtMetodeStoetterCriiptoOpenId = computed(() => [Autentiseringsmetode.passord, Autentiseringsmetode.smsOtp, Autentiseringsmetode.totpOtp, Autentiseringsmetode.criiptoOpenId].includes(this.valgtInnlogging()));
    valgtMetodeStoetterPassord = computed(() => this.valgtInnlogging() === Autentiseringsmetode.passord);
    valgtMetodeStoetterSms = computed(() => [Autentiseringsmetode.passord, Autentiseringsmetode.smsOtp].includes(this.valgtInnlogging()));
    valgtMetodeStoetterTotpOtp = computed(() => [Autentiseringsmetode.passord, Autentiseringsmetode.smsOtp, Autentiseringsmetode.totpOtp].includes(this.valgtInnlogging()));

    kontaktPerson = signal<EnkelBrukerModell | null>(null);

    @Input({required: true}) set gruppe(gruppe: GruppeModell) {
        this.gruppeModell = gruppe;
        if (this.gruppeModell) {
            this.settAktivertHosBruker(this.avhengigheter.autentisertBruker()?.autentiseringsmetoder ?? []);
            this.kanRedigere.set(this.avhengigheter.inneharTilgangsrolleIGruppe(this.gruppeModell.id));
            this.valgtInnlogging.set(this.finnLavesteInnloggingsNivå(this.gruppeModell.tillatteAutentiseringsmetoder ?? []));
        }
    }

    constructor(private avhengigheter: GruppeTillatteAutentiseringsmetoderAvhengigheter) {
    }

    rediger() {
        this.modus.set('rediger');
    }

    lagre() {
        const valgteMetoder = this.valgteMetoder();

        const brukerMetoder = this.avhengigheter.autentisertBruker()?.autentiseringsmetoder ?? [];
        const aktivBrukerMisterTilgang = brukerMetoder.filter(m => valgteMetoder.includes(m)).length === 0;

        if (aktivBrukerMisterTilgang) {
            this.avhengigheter.bekreftMisterTilgang()
              .then(bekreftet => {
                  if (bekreftet) {
                      this.utførLagre(aktivBrukerMisterTilgang);
                  } else {
                      this.avbryt();
                  }
              })
              .catch(() => this.avbryt());
        } else {
            this.utførLagre(aktivBrukerMisterTilgang);
        }
    }

    avbryt() {
        this.valgtInnlogging.set(this.finnLavesteInnloggingsNivå(this.gruppeModell.tillatteAutentiseringsmetoder ?? []));
        this.modus.set('vis');

    }

    hoyestInnloggingsNivaaForBruker(): Autentiseringsmetode {
        return this.finnHoeyesteInnloggingsNivaa(this.avhengigheter.autentisertBruker().autentiseringsmetoder);
    }

    brukerHarHoeyereNivaa(autentiseringsmetode: Autentiseringsmetode): boolean {
        const hoyest = this.hoyestInnloggingsNivaaForBruker();
        for (const metode of this.alleMetoderSortertEtterinnloggingsNivaa) {
            if (metode === autentiseringsmetode) return true;
            if (metode === hoyest) return false;
        }
        return false;
    }

    private finnHoeyesteInnloggingsNivaa(autentiseringsmetoder: Autentiseringsmetode[]): Autentiseringsmetode {
        const reversert = [...this.alleMetoderSortertEtterinnloggingsNivaa].reverse();
        for (const autentiseringsmetode of reversert) {
            if (autentiseringsmetoder.includes(autentiseringsmetode)) return autentiseringsmetode;
        }
        console.log("Fant ikke innloggingsmetode i ", autentiseringsmetoder);
        return Autentiseringsmetode.criiptoOpenId;
    }

    private valgteMetoder(): Autentiseringsmetode[] {
        const valgteMetoder: Autentiseringsmetode[] = [];
        if (this.valgtMetodeStoetterCriiptoOpenId()) {
            valgteMetoder.push(Autentiseringsmetode.criiptoOpenId)
        }
        if (this.valgtMetodeStoetterPassord()) {
            valgteMetoder.push(Autentiseringsmetode.passord)
        }
        if (this.valgtMetodeStoetterSms()) {
            valgteMetoder.push(Autentiseringsmetode.smsOtp)
        }
        if (this.valgtMetodeStoetterTotpOtp()) {
            valgteMetoder.push(Autentiseringsmetode.totpOtp)
        }

        return valgteMetoder;
    }

    private settAktivertHosBruker(velgbareMetoder: Autentiseringsmetode[] = []) {
        console.log('velgbareMetoder', velgbareMetoder);
        for (const metode of this.metoder) {
            metode.aktivertHosBruker = velgbareMetoder.includes(metode.metode);
        }
    }

    private utførLagre(aktivBrukerMisterTilgang: boolean) {
        this.avhengigheter.visReautentiseringDialog(
          autentiseringsinfo => this.validerAutentiseringsinfoOgOppdaterTillatteAutmetoder(aktivBrukerMisterTilgang, autentiseringsinfo)
        );
    }

    private finnLavesteInnloggingsNivå(autentiseringsmetoder: Autentiseringsmetode[]): Autentiseringsmetode {
        for (const autentiseringsmetode of this.alleMetoderSortertEtterinnloggingsNivaa) {
            if (autentiseringsmetoder.includes(autentiseringsmetode)) return autentiseringsmetode;
        }
        console.log("Fant ikke innloggingsmetode i ", autentiseringsmetoder);
        return Autentiseringsmetode.passord;
    }

    private somSpesifikasjon(autentiseringsinfo: Reautentiseringsinformasjon): TillatteAutentiseringsmetoderSpesifikasjon {
        const valgteMetoder = this.valgteMetoder();
        return {
            metoder: valgteMetoder,
            autentiseringsinfo: autentiseringsinfo
        };
    }

    private validerAutentiseringsinfoOgOppdaterTillatteAutmetoder(aktivBrukerMisterTilgang: boolean, autentiseringsinfo: Reautentiseringsinformasjon): Promise<boolean> {
        return this.avhengigheter.oppdaterTillatteAutentiseringsmetoder(this.gruppeModell.id, this.somSpesifikasjon(autentiseringsinfo))
          .then(respons => {
              if (respons.autentiseringOk) {
                  this.gruppeModell.tillatteAutentiseringsmetoder = respons.tillatteAutentiseringsmetoder;
                  this.modus.set('vis');
                  this.avhengigheter.relastKontekst();
                  if (aktivBrukerMisterTilgang) {
                      this.avhengigheter.gåTilGrupper();
                  }
              }
              return respons.autentiseringOk;
          });
    }
}

@Injectable()
export class GruppeTillatteAutentiseringsmetoderAvhengigheter {
    constructor(
      private brukerkontekstTilbyder: BrukerkontekstTilbyder,
      private gruppeRessurs: GruppeRessursClient,
      private navigasjon: Navigasjon,
      private dialog: Dialog,
      private oversettelser: Oversettelser,
      private matDialog: MatDialog,
      private brukerkontekstAdministrasjon: BrukerkontekstAdministrasjon
    ) {
    }

    autentisertBruker(): BrukerModell {
        return this.brukerkontekstTilbyder.autentisertBruker().bruker;
    }

    inneharTilgangsrolleIGruppe(gruppeId: GruppeId): boolean {
        return this.brukerkontekstTilbyder.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, gruppeId);
    }

    bekreftMisterTilgang(): Promise<boolean> {
        return this.dialog.advarsel({
            tittel: this.oversettelser.oversett(i18n('Du kan være i ferd med å stenge deg selv ute fra gruppen')),
            innhold: this.oversettelser.oversett(i18n('Du har ikke aktivert noen av de valgte innloggingsmetodene på din egen brukerkonto. Du vil dermed miste tilgang til gruppen inntil du aktiverer en av de valgte metodene på din brukerkonto.')),
            okKnapp: this.oversettelser.oversett(i18n('Fortsett')),
            avbrytKnapp: this.oversettelser.oversett(i18n('Avbryt'))
        });
    }

    visReautentiseringDialog(autentiser: Autentiser): void {
        ReautentiseringDialogKomponent.vis(
          this.matDialog,
          this.oversettelser.oversett(i18n('Bekreft identiteten din for å oppdatere tillatte autentiseringsmetoder')),
          autentiser
        );
    }

    oppdaterTillatteAutentiseringsmetoder(gruppeId: GruppeId, spesifikasjon: TillatteAutentiseringsmetoderSpesifikasjon): Promise<TillatteAutentiseringsmetoderOppdatertModell> {
        return this.gruppeRessurs.oppdaterTillatteAutentiseringsmetoder(gruppeId, spesifikasjon).somPromise();
    }

    relastKontekst(): void {
        this.brukerkontekstAdministrasjon.relastKontekst();
    }

    gåTilGrupper(): void {
        this.navigasjon.gåTilGrupper();
    }
}

export interface MetodeAktiverHosBruker {
    metode: Autentiseringsmetode;
    aktivertHosBruker: boolean;
}
